import React from 'react';
import { ObservableField as Field, useForm } from '../../ObservableForm';
import Bugsnag from '@bugsnag/js';
import { SelectChoiceFieldFragment } from './SelectChoiceField.generated';
import { Select } from '@99designs/spa-common';
import * as Types from '@99designs/graph-utils/types';

type Option = {
    label: string;
    subtext?: Types.Maybe<string>;
    value: string;
    group?: Types.Maybe<string>;
};

interface OptionsProps {
    options: Option[];
}

interface OptionGroupsData {
    [key: string]: Option[];
}

function Options({ options }: OptionsProps) {
    return (
        <>
            {options.map(({ label, value }: Option) => (
                <option key={value} value={value}>
                    {label}
                </option>
            ))}
        </>
    );
}

function GroupedOptions({ options }: OptionsProps) {
    const groups = options.reduce((result: OptionGroupsData, option: Option) => {
        if (option['group']) {
            (result[option['group']] = result[option['group']] || []).push(option);
        }
        return result;
    }, {});

    return (
        <>
            {Object.keys(groups).map((group) => (
                <optgroup label={group} key={group}>
                    <Options options={groups[group]} />
                </optgroup>
            ))}
        </>
    );
}

export type Props = SelectChoiceFieldFragment;

export function Edit({ label, subtext, tooltip, placeholder, id, options, required }: Props) {
    const form = useForm();
    // eslint-disable-next-line eqeqeq
    const hasGroups = options[0].group != '';

    return (
        <Field
            form={form}
            name={id}
            title={label}
            message={subtext ?? ''}
            required={required ?? false}
            tooltip={tooltip ? tooltip : undefined}
            render={() => (
                <Select
                    name={id}
                    value={form.get(id) || ''}
                    onChange={(e) => {
                        form.set(id, e.target.value, true).catch(Bugsnag.notify);
                    }}
                    fullWidth
                >
                    {placeholder && (
                        <option value="" disabled={true}>
                            {placeholder}
                        </option>
                    )}

                    {hasGroups ? (
                        <GroupedOptions options={options} />
                    ) : (
                        <Options options={options} />
                    )}
                </Select>
            )}
        />
    );
}

export function View({ options, choiceValue }: Props) {
    const option = options.find((o) => o.value === choiceValue);
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{option?.label}</>;
}

export function HasValue({ choiceValue }: Props): boolean {
    return choiceValue !== '';
}

export function getValue({ choiceValue }: Props) {
    return choiceValue;
}
